import React from 'react';
import {
  Flex,
  GoodsCarousel,
  GoodsCarouselSkeleton,
  type GoodsData,
  Headline,
  List,
  useSafeArea,
} from '@banhala/ui-kit';
import { useGoodsLikeRecommend } from '../queries/useGoodsLikeRecommend';
import { useAddLikeGoods, useLikeMap } from 'shared/src/hooks';
import { useLogDisplayGoods } from '../../../../../hooks/useLogDisplayGoods';
import {
  bridge,
  LOG_ANALYTICS_ALL_CHANNELS,
  useNavigator,
} from 'shared/src/utils';
import type { LogAnalyticsEventParams } from 'shared/src/utils/bridge/logAnalyticsEvent/types';
import { useRemoveLikeGoods } from '../../../queries/useRemoveLikeGoods';
import { useLogDisplayCarousel } from '../hooks/useLogDisplayCarousel';
import type {
  AddCartRecommendItem,
  GoodsLikeRecommendItem,
} from 'shared/src/api';

const GoodsCarouselItem: React.FC<{
  isLogEnabled: boolean;
  carousel: {
    item_list: (
      | (GoodsData & AddCartRecommendItem)
      | (GoodsData & GoodsLikeRecommendItem)
    )[];
    title: string;
  };
}> = props => {
  const { logDisplayGoods } = useLogDisplayGoods({
    enabled: props.isLogEnabled,
  });

  const { likeMap, setLikeMap } = useLikeMap(
    props.carousel.item_list.flat(1).reduce((acc, val) => {
      return {
        ...acc,
        [val.item.sno]: val.item.like.is_liked,
      };
    }, {}) ?? {},
  );

  const { addLikeGoods } = useAddLikeGoods({
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen: 'GOODS_DETAIL',
  });
  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { navigator } = useNavigator();

  return (
    <Flex>
      <Headline
        headline={props.carousel.title}
        size={'medium'}
        topCompact={true}
      />
      <Flex>
        <GoodsCarousel
          list={props.carousel.item_list}
          onPressItem={async (pressed, index) => {
            const { item, logging } = pressed;
            bridge.logAnalyticsEvent({
              name: 'CLICK_GOODS',
              ably_only_log: logging.ably_only_log,
              analytics: {
                SCREEN: 'GOODS_DETAIL',
                ...logging.analytics,
                INDEX: index,
              },
            });
            navigator.moveToGoods({
              sno: item.sno,
              firstPageRendering: item.first_page_rendering,
            });
          }}
          likeMap={likeMap}
          onPressLike={async (pressed, index) => {
            const { item, logging } = pressed;

            const analytics: LogAnalyticsEventParams['analytics'] = {
              SCREEN: 'GOODS_DETAIL',
              ...logging.analytics,
              INDEX: index,
            };

            if (!likeMap[item.sno]) {
              bridge.logAnalyticsEvent({
                name: 'CLICK_LIKE_GOODS',
                ably_only_log: logging.ably_only_log,
                channel: LOG_ANALYTICS_ALL_CHANNELS,
                analytics,
              });
              // 좋아요 누르기
              addLikeGoods(item.sno, {
                SCREEN: 'GOODS_DETAIL',
              });
              return;
            }

            bridge.logAnalyticsEvent({
              name: 'CLICK_UNLIKE_GOODS',
              ably_only_log: logging.ably_only_log,
              analytics,
            });

            await mutateRemoveLikeGoods({
              sno: item.sno,
            });

            //해제하기
            bridge.likeGoods({
              goods_sno: item.sno,
              is_liked: false,
            });
            setLikeMap(prevState => ({
              ...prevState,
              [item.sno]: false,
            }));
          }}
          size={'small'}
          onViewableItemsChanged={logDisplayGoods}
        />
      </Flex>
    </Flex>
  );
};

const GoodsLikeRecommendContent: React.FC<{
  isExpanded: boolean;
}> = props => {
  const { data: goodsLikeRecommend } = useGoodsLikeRecommend();
  const { bottom } = useSafeArea();

  const [contentHeight, setContentHeight] = React.useState(0);

  useLogDisplayCarousel();
  return (
    <List
      style={{ height: contentHeight }}
      data={goodsLikeRecommend.carousels}
      onContentSizeChange={(w, h) => {
        setContentHeight(h);
      }}
      contentContainerStyle={{
        paddingBottom: bottom,
      }}
      scrollEnabled={props.isExpanded}
      renderItem={({ item: carousel, index }) => {
        return (
          <GoodsCarouselItem
            carousel={carousel}
            isLogEnabled={(props.isExpanded && index !== 0) || index === 0}
          />
        );
      }}
    />
  );
};

const GoodsLikeRecommendSkeleton = () => (
  <Flex>
    <Flex padding={{ horizontal: 16, vertical: 16 }}>
      <Flex width={300} height={20} backgroundColor={'bg_tertiary'} />
    </Flex>
    <Flex height={200}>
      <GoodsCarouselSkeleton size={'small'} count={4} />
    </Flex>
    <Flex padding={{ horizontal: 16, vertical: 16 }}>
      <Flex width={300} height={20} backgroundColor={'bg_tertiary'} />
    </Flex>
    <Flex height={200}>
      <GoodsCarouselSkeleton size={'small'} count={4} />
    </Flex>
  </Flex>
);

export const GoodsLikeRecommend: React.FC<{
  isExpanded: boolean;
}> = props => {
  return (
    <React.Suspense fallback={<GoodsLikeRecommendSkeleton />}>
      <GoodsLikeRecommendContent {...props} />
    </React.Suspense>
  );
};
